import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { chatIaAPI } from '@/lib/api';
import {
  Bot, Send, Plus, Trash2, Loader2, MessageSquarePlus, Sparkles, User
} from 'lucide-react';

const ChatIA = () => {
  const { user } = useAuth();
  const [sessoes, setSessoes] = useState([]);
  const [sessaoAtiva, setSessaoAtiva] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [loadingSessoes, setLoadingSessoes] = useState(true);
  const [streamContent, setStreamContent] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [mensagens, streamContent, scrollToBottom]);

  // Carregar sessões
  useEffect(() => {
    carregarSessoes();
  }, []);

  const carregarSessoes = async () => {
    try {
      setLoadingSessoes(true);
      const res = await chatIaAPI.listarSessoes();
      setSessoes(res.data);
    } catch (e) {
      console.error('Erro ao carregar sessões:', e);
    } finally {
      setLoadingSessoes(false);
    }
  };

  const criarNovaSessao = async () => {
    try {
      const res = await chatIaAPI.criarSessao();
      const novaSessao = res.data;
      setSessoes(prev => [novaSessao, ...prev]);
      selecionarSessao(novaSessao);
    } catch (e) {
      console.error('Erro ao criar sessão:', e);
    }
  };

  const selecionarSessao = async (sessao) => {
    setSessaoAtiva(sessao);
    setStreamContent('');
    try {
      const res = await chatIaAPI.listarMensagens(sessao.session_id);
      setMensagens(res.data);
    } catch (e) {
      console.error('Erro ao carregar mensagens:', e);
      setMensagens([]);
    }
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const excluirSessao = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Excluir esta conversa?')) return;
    try {
      await chatIaAPI.excluirSessao(sessionId);
      setSessoes(prev => prev.filter(s => s.session_id !== sessionId));
      if (sessaoAtiva?.session_id === sessionId) {
        setSessaoAtiva(null);
        setMensagens([]);
      }
    } catch (e) {
      console.error('Erro ao excluir sessão:', e);
    }
  };

  const enviarMensagem = async () => {
    if (!input.trim() || streaming || !sessaoAtiva) return;

    const texto = input.trim();
    setInput('');

    // Adicionar msg do user imediatamente na UI
    const userMsg = { role: 'user', content: texto, criado_em: new Date().toISOString() };
    setMensagens(prev => [...prev, userMsg]);

    setStreaming(true);
    setStreamContent('');

    // Atualizar título na lista se for primeira mensagem
    if (mensagens.filter(m => m.role === 'user').length === 0) {
      const titulo = texto.substring(0, 50) + (texto.length > 50 ? '...' : '');
      setSessoes(prev => prev.map(s =>
        s.session_id === sessaoAtiva.session_id ? { ...s, titulo } : s
      ));
    }

    try {
      const response = await chatIaAPI.enviarMensagemStream(sessaoAtiva.session_id, texto);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              // Stream completo
              setMensagens(prev => [...prev, { role: 'assistant', content: fullContent, criado_em: new Date().toISOString() }]);
              setStreamContent('');
            } else {
              fullContent += data;
              setStreamContent(fullContent);
            }
          }
        }
      }
    } catch (e) {
      console.error('Erro no stream:', e);
      setMensagens(prev => [...prev, { role: 'assistant', content: 'Erro ao obter resposta da IA. Tente novamente.', criado_em: new Date().toISOString() }]);
      setStreamContent('');
    } finally {
      setStreaming(false);
      inputRef.current?.focus();
    }
  };

  const formatarData = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatarDataSessao = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString()) return 'Hoje';
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    if (d.toDateString() === ontem.toDateString()) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="h-[calc(100vh-120px)]" data-testid="chat-ia-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assistente IA</h1>
            <p className="text-gray-500 text-sm">Powered by GPT-4o</p>
          </div>
        </div>
      </div>

      {/* Container Principal */}
      <Card className="h-full overflow-hidden">
        <div className="flex h-full">
          {/* Sidebar - Lista de Sessões */}
          <div className="w-72 border-r flex flex-col bg-white">
            <div className="p-3 border-b">
              <Button
                onClick={criarNovaSessao}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="new-chat-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Conversa
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingSessoes ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
                </div>
              ) : sessoes.length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">
                  <MessageSquarePlus className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p>Nenhuma conversa ainda</p>
                  <p className="text-xs mt-1">Clique em "Nova Conversa" para começar</p>
                </div>
              ) : (
                sessoes.map((sessao) => (
                  <div
                    key={sessao.session_id}
                    onClick={() => selecionarSessao(sessao)}
                    data-testid={`chat-session-${sessao.session_id}`}
                    className={`flex items-center justify-between px-3 py-3 cursor-pointer hover:bg-gray-50 border-b group transition-colors ${
                      sessaoAtiva?.session_id === sessao.session_id ? 'bg-violet-50 border-l-4 border-l-violet-600' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="text-sm font-medium text-gray-800 truncate">{sessao.titulo}</p>
                      <p className="text-xs text-gray-400">{formatarDataSessao(sessao.atualizado_em)}</p>
                    </div>
                    <button
                      onClick={(e) => excluirSessao(e, sessao.session_id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-1"
                      data-testid={`delete-session-${sessao.session_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Área do Chat */}
          <div className="flex-1 flex flex-col bg-gray-50">
            {sessaoAtiva ? (
              <>
                {/* Header */}
                <div className="bg-violet-600 px-4 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Assistente Multi Telecom</p>
                    <p className="text-xs text-violet-200">GPT-4o • Pronto para ajudar</p>
                  </div>
                </div>

                {/* Mensagens */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {mensagens.length === 0 && !streamContent && (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center max-w-md">
                        <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Sparkles className="h-8 w-8 text-violet-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Como posso ajudar?</h3>
                        <p className="text-gray-500 text-sm mb-4">
                          Pergunte sobre vendas, processos, produtos ou qualquer dúvida do CRM.
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                          {['Como preencher uma nova venda?', 'Quais são os status de venda?', 'Como funciona o mailing?'].map((sugestao, i) => (
                            <button
                              key={i}
                              onClick={() => { setInput(sugestao); inputRef.current?.focus(); }}
                              className="text-left text-sm px-3 py-2 rounded-lg border border-violet-200 hover:bg-violet-50 text-violet-700 transition-colors"
                              data-testid={`suggestion-${i}`}
                            >
                              {sugestao}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {mensagens.map((msg, idx) => (
                    <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <Bot className="h-4 w-4 text-violet-600" />
                        </div>
                      )}
                      <div className={`max-w-[70%] px-4 py-3 rounded-2xl shadow-sm ${
                        msg.role === 'user'
                          ? 'bg-violet-600 text-white rounded-br-md'
                          : 'bg-white text-gray-800 rounded-bl-md border'
                      }`}>
                        <p className="whitespace-pre-wrap break-words text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 text-right ${msg.role === 'user' ? 'text-violet-200' : 'text-gray-400'}`}>
                          {formatarData(msg.criado_em)}
                        </p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Streaming content */}
                  {streamContent && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="h-4 w-4 text-violet-600" />
                      </div>
                      <div className="max-w-[70%] px-4 py-3 rounded-2xl shadow-sm bg-white text-gray-800 rounded-bl-md border">
                        <p className="whitespace-pre-wrap break-words text-sm">{streamContent}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                          <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                          <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Loading indicator (before first stream token) */}
                  {streaming && !streamContent && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="h-4 w-4 text-violet-600" />
                      </div>
                      <div className="px-4 py-3 rounded-2xl bg-white border shadow-sm">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                          <span className="text-sm text-gray-500">Pensando...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="bg-white border-t px-4 py-3 flex items-center gap-3">
                  <Input
                    ref={inputRef}
                    placeholder="Digite sua pergunta..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && enviarMensagem()}
                    className="flex-1"
                    disabled={streaming}
                    data-testid="chat-ia-input"
                  />
                  <Button
                    onClick={enviarMensagem}
                    disabled={!input.trim() || streaming}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="chat-ia-send-btn"
                  >
                    {streaming ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </>
            ) : (
              /* Welcome screen */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-md">
                  <div className="w-24 h-24 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Bot className="w-12 h-12 text-violet-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">Assistente IA</h2>
                  <p className="text-gray-500 mb-6">
                    Use inteligência artificial para tirar dúvidas, criar textos e agilizar seu trabalho no CRM.
                  </p>
                  <Button
                    onClick={criarNovaSessao}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="welcome-new-chat-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Iniciar Conversa
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChatIA;
